<script lang="ts">
  import type { DocumentInfo } from '$lib/storage/types'
  
  // Action to select text on focus
  function selectText(node: HTMLInputElement) {
    setTimeout(() => {
      node.focus()
      node.select()
    }, 10)
  }
  
  interface Props {
    id: string
    name: string
    document?: DocumentInfo
    isReadError?: boolean
    isImage?: boolean
    extension?: string
    depth: number
    isOpen: boolean
    isSelected: boolean
    isFocused: boolean
    isDragged: boolean
    onSelect: (event: MouseEvent) => void
    onContextMenu: (event: MouseEvent) => void
    onDragStart: (event: DragEvent) => void
    onDragEnd: () => void
    onStartEdit: () => void
    editingNodeId: string | null
    editingTitle: string
    editingDuplicateWarning?: string
    onFinishEdit: () => void
    onEditKeydown: (event: KeyboardEvent) => void
    onEditTitleChange?: (value: string) => void
  }
  
  let {
    id,
    name,
    document,
    isReadError = false,
    isImage = false,
    extension,
    depth,
    isOpen,
    isSelected,
    isFocused,
    isDragged,
    onSelect,
    onContextMenu,
    onDragStart,
    onDragEnd,
    onStartEdit,
    editingNodeId,
    editingTitle,
    editingDuplicateWarning = '',
    onFinishEdit,
    onEditKeydown,
    onEditTitleChange
  }: Props = $props()
  
</script>

<div
  class="file-node {depth === 0 ? 'root-file' : ''} {isOpen ? 'open' : ''} {isSelected ? 'selected' : ''} {isFocused ? 'focused' : ''} {isDragged ? 'dragged' : ''}"
  data-file-id={id}
  data-file-name={name}
  data-depth={depth}
  data-is-selected={isSelected}
  data-is-focused={isFocused}
  draggable={editingNodeId !== id}
  role="treeitem"
  aria-selected={isSelected}
  tabindex="0"
  ondragstart={onDragStart}
  ondragend={onDragEnd}
  onclick={onSelect}
  onkeydown={(e) => {
    // Don't handle keyboard events if we're editing
    if (editingNodeId === id) {
      return
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onSelect(e as any)
    }
  }}
  oncontextmenu={onContextMenu}
>
  {#if editingNodeId === id}
    <div class="edit-input-wrapper">
      <input
        value={editingTitle}
        oninput={(e) => onEditTitleChange?.(e.currentTarget.value)}
        onblur={onFinishEdit}
        onkeydown={(e) => {
          // Stop propagation for all keys to prevent parent handlers
          e.stopPropagation()
          // Then call the edit keydown handler for Enter/Escape
          onEditKeydown(e)
        }}
        onkeyup={(e) => {
          // Also stop keyup propagation to be safe
          e.stopPropagation()
        }}
        onclick={(e) => e.stopPropagation()}
        autocapitalize="off"
        autocorrect="off"
        spellcheck="false"
        class="edit-input {editingDuplicateWarning ? 'has-warning' : ''}"
        use:selectText
      />
    </div>
    {#if editingDuplicateWarning}
      <div class="duplicate-warning">{editingDuplicateWarning}</div>
    {/if}
  {:else}
    <span class="node-name" data-node-id={id} data-unique-key={id} title={name}>{name}</span>
    {#if extension}
      <span class="file-extension">{extension.toUpperCase()}</span>
    {/if}
  {/if}
</div>

<style>
  .file-node {
    width: 100%;
    padding: 4px 12px 4px 24px;
    display: flex;
    align-items: center;
    gap: 6px;
    background: transparent;
    border: none;
    color: var(--text-secondary);
    cursor: pointer;
    text-align: left;
    position: relative;
    user-select: none;
    overflow: visible;
  }
  
  .file-node.root-file {
    padding-left: 30px;
  }
  
  .file-node:hover {
    background: var(--bg-tertiary);
  }
  
  .file-node.open {
    color: var(--text-primary);
    font-weight: 500;
  }
  
  
  .file-node.selected {
    background: rgba(79, 70, 229, 0.15);
    border-left: 2px solid var(--accent-primary);
    padding-left: 24px;
  }

  .file-node.root-file.selected {
    padding-left: 30px;
  }
  
  /* Show dragged files with active appearance */
  .file-node.dragged {
    background: rgba(79, 70, 229, 0.08);
    border: 1px dashed rgba(79, 70, 229, 0.4);
    border-radius: 4px;
    opacity: 0.85;
    position: relative;
  }
  
  .file-node.dragged::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(90deg, 
      transparent, 
      rgba(79, 70, 229, 0.05), 
      transparent
    );
    pointer-events: none;
  }
  
  .file-node.focused {
    background: rgba(156, 163, 175, 0.08);
    box-shadow: inset 0 0 0 1px rgba(156, 163, 175, 0.2);
    border-radius: 4px;
  }
  
  .node-name {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
    padding-right: 4px;
  }
  
  .file-extension {
    font-size: var(--ui-font-size-xs);
    color: var(--text-tertiary);
    padding: 2px 4px;
    background: var(--bg-tertiary);
    border-radius: 3px;
    margin-left: auto;
    flex-shrink: 0;
  }
  
  .edit-input-wrapper {
    flex: 1;
    position: relative;
    min-width: 0;
    overflow: visible;
  }
  
  .edit-input {
    width: 100%;
    padding: 2px 6px;
    margin: 0 2px;
    background: var(--bg-primary);
    border: 1px solid var(--accent-primary);
    border-radius: 3px;
    color: var(--text-primary);
    font-size: var(--ui-font-size-md);
    font-weight: 400;
    outline: none;
    box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.2);
  }
  
  .edit-input.has-warning {
    border-color: var(--error, #ef4444);
    box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.2);
  }
  
  .duplicate-warning {
    position: absolute;
    top: 100%;
    left: 24px;
    margin-top: 2px;
    padding: 6px 12px;
    background: var(--error, #ef4444);
    color: white;
    font-size: 12px;
    font-weight: 500;
    border-radius: 4px;
    z-index: 10000;
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3),
                0 2px 4px rgba(0, 0, 0, 0.2);
    animation: warningFadeIn 0.2s ease-out;
    line-height: 1.4;
    white-space: nowrap;
    width: fit-content;
    max-width: calc(100vw - 48px);
  }

  @keyframes warningFadeIn {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  :global(.error-icon) {
    color: var(--error) !important;
  }
</style>